import type { TreeNode } from 'tree-model';

import {
  OBJECT_TYPE_COLLECTIONS,
  type ObjectType,
} from '@iconik-io/iconik-api/src/types/object_types';
import type { SearchItemEntity } from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';

import type { TreeReducerModel } from '../../ducks/tree/types';
import {
  getParentCollectionsFromTree,
  isCollectionMappedToStorage,
} from '../../utils/collections';

/**
 * @deprecated
 */
export function getObjectLinkDeprecated(
  id?: string,
  objectType?: ObjectType,
  versionId = '',
): string {
  if (id && objectType) {
    return `/${
      objectType === OBJECT_TYPE_COLLECTIONS ? 'collection' : 'asset'
    }/${id}${versionId ? '/v/' : ''}${versionId}`;
  }
  return '/search';
}

// The walk starts from an event target, which callers read as possibly absent.
export function findAncestorWithAttr(
  element: Element | null | undefined,
  attribute: string,
  value?: string,
): Element | null {
  let el: Element | null | undefined = element;
  while (el && el.getAttribute) {
    if (value && el && el.getAttribute(attribute) === value) {
      return el;
    }

    if (!value && el && el.getAttribute(attribute)) {
      return el;
    }

    el = el.parentElement;
  }
  return null;
}

export function findAncestorWithId(
  element: Element | null | undefined,
): Element | null {
  return findAncestorWithAttr(element, 'data-id');
}

export function eventElement(target: EventTarget | null): Element | null {
  return target instanceof Element ? target : null;
}

export function findAncestorId(
  element: Element | null | undefined,
): string | null {
  const el = findAncestorWithId(element);
  if (el) {
    return el.getAttribute('data-id');
  }

  return null;
}

function checkForMappedCollectionParents(
  collection: SearchItemEntity | undefined,
  flatTree: TreeNode<TreeReducerModel>[],
) {
  if (isCollectionMappedToStorage(collection)) {
    return true;
  }

  if (collection?.parents) {
    const collectionParentNodes = collection.parents.map((parentId) =>
      flatTree.find((c) => c.model.id === parentId),
    );
    return collectionParentNodes
      .flatMap(getParentCollectionsFromTree)
      .some(isCollectionMappedToStorage);
  }

  if (collection?.parent_id) {
    const collectionParentNode = flatTree.find(
      (each) => each.model.id === collection.parent_id,
    );
    return getParentCollectionsFromTree(collectionParentNode).some(
      isCollectionMappedToStorage,
    );
  }

  const collectionNode = flatTree.find((c) => c.model.id === collection?.id);
  return getParentCollectionsFromTree(collectionNode).some(
    isCollectionMappedToStorage,
  );
}

export function isParentMappedCollection(
  items: SearchItemEntity[],
  flatTree: TreeNode<TreeReducerModel>[],
) {
  const collections = items.filter(
    (each) => each?.object_type === 'collections',
  );

  if (collections.length === 0) {
    return false;
  }

  return collections.some((c) => checkForMappedCollectionParents(c, flatTree));
}
